import React, { useEffect, useState } from 'react';

import { useParams } from 'react-router';

import Chip from '../../components/Chip';
import ContentGroupsList from '../../components/ContentGroupsList';
import ContentWrapper from '../../components/ContentWrapper';
import DetailsModal, { Values } from '../../components/DetailsModal';
import { NoPermissionMessage } from '../../components/PageLayout';
import SkeletonStack from '../../components/SkeletonStack';
import {
  useGetExamsQuery, useGetExamTypesQuery, useNewExamMutation,
} from '../../services/exams';
import { ShortExam } from '../../services/exams/types';
import { PermissionType } from '../../types/common';
import {
  SubjectDetails, SUBJECTS,
} from '../../utils/common/constants';
import hasSubjectPermission from '../../utils/common/hasSubjectPermission';
import useUserPermissions from '../../utils/react/hooks/useUserPermissions';

import {
  PageContainer,
} from './index.styles';

const INITIAL_EXAM_FORM: Values = {
  label: '',
  shortLabel: '',
  type: '',
};

const AdminExamsPage = () => {
  const { subject: querySubject } = useParams();
  const permissions = useUserPermissions();

  // null indicates the subject was fetched, but the user does not have permission
  const [subject, setSubject] = useState<SubjectDetails | undefined | null>(undefined);
  useEffect(() => {
    if (querySubject && permissions) {
      const subjectObj = SUBJECTS?.[querySubject];
      if (subjectObj && hasSubjectPermission(permissions, querySubject, PermissionType.Exam)) {
        setSubject(subjectObj);
      } else {
        setSubject(null);
      }
    }
  }, [querySubject, permissions]);

  /**
   * Exam Queries & Mutations
   */
  const { data: exams, isFetching } = useGetExamsQuery({ subject: subject?.key || '', includeInactive: true },
    { skip: !subject });

  const {
    data: examTypes,
    isLoading: isExamTypesLoading,
  } = useGetExamTypesQuery({ subject: subject?.key || '' },
    { skip: !subject });
  const [newExamFormOpen, setNewExamFormOpen] = useState(false);
  const [newExamForm, setNewExamForm] = useState<Values>(INITIAL_EXAM_FORM);
  const [newExam,
    {
      isSuccess: isNewExamSuccess,
      isLoading: isNewExamLoading,
      error: newExamError,
      reset: resetNewExam,
    }] = useNewExamMutation();

  const resetModal = async () => {
    await resetNewExam();
    setNewExamForm(INITIAL_EXAM_FORM);
  };

  // Close modal after exam is created
  useEffect(() => {
    if (isNewExamSuccess) {
      setNewExamFormOpen(false);
      resetModal();
    }
  }, [isNewExamSuccess]);

  /**
   * Exam functions
   */

  const onExamCreate = (values?: Values) => {
    if (values?.label && values?.type && subject?.key) {
      newExam({
        subject: subject.key,
        label: values.label,
        shortLabel: values.shortLabel,
        type: values.type,
      });
    }
  };

  if (subject === undefined || isExamTypesLoading) {
    return (
      <SkeletonStack />
    );
  }

  if (subject === null) {
    return (
      <NoPermissionMessage />
    );
  }

  return (
    <PageContainer>
      <ContentWrapper title="Exams" backButtonHref="/admin/subjects">
        <ContentGroupsList
          columns={[
            {
              field: 'label',
              headerName: 'Exam',
              width: 480,
            },
            {
              field: 'active',
              headerName: 'State',
              renderCell: ({ row }: any) => (
                <Chip
                  size="small"
                  label={row.active ? 'Active' : 'Inactive'}
                  color={row.active ? 'success' : 'error'}
                />
              ),
            },
          ]}
          data={exams?.data || []}
          loading={isFetching}
          actions={{
            create: {
              fn: () => setNewExamFormOpen(true),
              label: 'Add Exam',
            },
          }}
          getRowHref={(row: ShortExam) => row.examId}
        />
      </ContentWrapper>
      <DetailsModal
        open={newExamFormOpen}
        handleClose={() => setNewExamFormOpen(false)}
        error={newExamError}
        title="Add New Exam"
        fields={{
          label: {
            editable: true,
            type: 'text',
            label: 'Exam Name',
          },
          shortLabel: {
            editable: true,
            type: 'text',
            label: 'Short Label',
          },
          type: {
            editable: true,
            type: 'select',
            label: 'Type',
            typeOptions: [...(examTypes?.data || [])]
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((entry) => ({
                label: entry.label,
                value: entry.type,
              })),
          },
        }}
        values={newExamForm}
        buttons={[
          {
            fn: onExamCreate,
            label: 'Add',
            loading: isNewExamLoading,
          },
        ]}
      />
    </PageContainer>
  );
};

export default AdminExamsPage;
